import type { NotifItem } from "../../context/NotificationsContext.js";

// One place that turns the API's raw notification into what every surface shows:
// the bell dropdown, the dashboard card and the toast all read this, so a title or
// a category can never differ between them.

export type NotifCategory = "leave" | "attendance" | "hours" | "other";
export type NotifStatus = "approved" | "declined" | "info";
export type NotifGroup = "Today" | "Earlier";

export interface ViewNotification {
  id: string;
  category: NotifCategory;
  /** Short bold headline, derived from `kind`. */
  title: string;
  /** The API's own message text, never rewritten. */
  body: string;
  time: string;
  group: NotifGroup;
  status: NotifStatus;
  read: boolean;
  link: string | null;
  createdAt: string;
}

/**
 * The spec names three categories, but the API emits ~40 kinds — assets, IT
 * tickets, probation, reviews and more. Those get `other` rather than being
 * forced into one of the three, which would mislabel them.
 */
export function categoryOf(kind: string): NotifCategory {
  if (kind.startsWith("leave_")) return "leave";
  if (kind.startsWith("attendance_")) return "attendance";
  if (kind.startsWith("weekly_hours_")) return "hours";
  return "other";
}

export function statusOf(kind: string): NotifStatus {
  if (/_(rejected|revoked|breached)$/.test(kind)) return "declined";
  if (/_(approved|confirmed)$/.test(kind) || kind === "resolved" || kind === "closed_ack") return "approved";
  return "info";
}

// Titles for the kinds users actually see most. Anything not listed falls back to
// a humanised form of the kind itself, so a newly added kind still reads sensibly
// instead of showing a raw snake_case string.
const TITLES: Record<string, string> = {
  leave_approved:                     "Leave approved",
  leave_rejected:                     "Leave declined",
  leave_revoked:                      "Leave revoked",
  leave_submitted:                    "Leave request submitted",
  attendance_approval_pending:        "Needs your approval",
  attendance_approval_approved:       "Approved",
  attendance_approval_rejected:       "Declined",
  attendance_overtime:                "Overtime",
  attendance_insufficient_hours:      "Short day",
  attendance_extended_hours:          "Long day",
  attendance_weekend_work:            "Weekend working",
  attendance_weekly_hours:            "Weekly hours short",
  attendance_shift_day_request:       "Hours request for a day",
  attendance_shift_day_decided:       "Hours request decided",
  attendance_shift_not_set:           "No shift set",
  attendance_shift_changed:           "Shift changed",
  attendance_correction_approved:     "Correction approved",
  attendance_correction_rejected:     "Correction declined",
  weekly_hours_approved:              "Weekly hours approved",
  weekly_hours_rejected:              "Weekly hours declined",
  weekly_hours_correction_approved:   "Hours correction approved",
  weekly_hours_correction_rejected:   "Hours correction declined",
  expense_submitted:                  "Expense claim submitted",
  expense_approval_reminder:          "Expense approval pending",
  expense_escalated:                  "Expense approval escalated",
  expense_approved:                   "Expense approved",
  expense_rejected:                   "Expense declined",
  expense_finance_approved:           "Expense finalised",
  COMP_VERIFICATION_REQUESTED:            "Compensation revision — verification requested",
  COMP_VERIFICATION_DECIDED:              "Compensation revision — Finance decision",
  COMP_STRUCTURE_VERIFICATION_REQUESTED:  "Compensation structure — verification requested",
  COMP_STRUCTURE_DECIDED:                 "Compensation structure — Finance decision",
};

export function titleOf(kind: string): string {
  const known = TITLES[kind];
  if (known) return known;
  // Sentence case, so an UPPER_SNAKE kind doesn't render as shouting capitals.
  const words = kind.replace(/_/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "just now", "12m", "3h", "2d", then a short date. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const mins = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function groupOf(iso: string, now: Date = new Date()): NotifGroup {
  const d = new Date(iso);
  return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate() ? "Today" : "Earlier";
}

export function toView(n: NotifItem, now: Date = new Date()): ViewNotification {
  return {
    id:        n.id,
    category:  categoryOf(n.kind),
    title:     titleOf(n.kind),
    body:      n.message,
    time:      relativeTime(n.createdAt, now),
    group:     groupOf(n.createdAt, now),
    status:    statusOf(n.kind),
    read:      n.readAt != null,
    link:      n.link,
    createdAt: n.createdAt,
  };
}

export interface CategoryCounts {
  all: number; leave: number; attendance: number; hours: number; other: number;
}

/** Counts always reflect the UNFILTERED list, per the spec. */
export function countsFor(items: ViewNotification[]): CategoryCounts {
  return items.reduce<CategoryCounts>((c, n) => {
    c.all += 1;
    c[n.category] += 1;
    return c;
  }, { all: 0, leave: 0, attendance: 0, hours: 0, other: 0 });
}

export function unreadCount(items: ViewNotification[]): number {
  return items.reduce((n, i) => n + (i.read ? 0 : 1), 0);
}

/** "9+" above nine. */
export function badgeText(count: number): string {
  return count > 9 ? "9+" : String(count);
}

/** Today first, then Earlier; empty groups are dropped by the caller. */
export function groupItems(items: ViewNotification[]): { group: NotifGroup; items: ViewNotification[] }[] {
  return (["Today", "Earlier"] as const)
    .map(group => ({ group, items: items.filter(i => i.group === group) }))
    .filter(g => g.items.length > 0);
}
